import { Circle } from '@mui/icons-material';
import { Chip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { EstateWorkItemStatus, EstateWorkItemSubStatus } from 'queries';

import { EstateWorkItemSubStatusExternalLabel } from 'utils/estate';

type Props = {
  estateWorkItemStatus: EstateWorkItemStatus;
  estateWorkItemSubStatus: EstateWorkItemSubStatus;
  onClick?: Function;
};

const useStyles = makeStyles(() => ({
  text: {
    fontSize: '12px',
    fontWeight: 'bold',
    lineHeight: '13px',
  },
  dot: {
    width: '6px',
    height: '6px',
  },
  chipGreen: {
    color: '#027A48',
    backgroundColor: '#ECFDF7',
  },
  chipLimeGreen: {
    color: '#000000',
    backgroundColor: '#03C04A',
  },
  chipLightGreen: {
    color: '#027A48',
    backgroundColor: '#ECFDF7',
  },
  chipBlack: {
    color: '#1C2541',
    backgroundColor: '#F9F9FB',
  },
  chipPurple: {
    color: '#5925DC',
    backgroundColor: '#F4F3FF',
  },
  chipYellow: {
    color: '#BE5C23',
    backgroundColor: '#FFFAEB',
  },
  chipDarkYellow: {
    color: '#000000',
    backgroundColor: '#FFD700',
  },
  chipRed: {
    color: '#B42318',
    backgroundColor: '#FEF3F2',
  },
  chipBlue: {
    color: '#175CD3',
    backgroundColor: '#EFF8FF',
  },
  chipDarkOrange: {
    color: '#FFF',
    backgroundColor: '#BE5504',
  },
  chipPink: {
    color: '#000000',
    backgroundColor: '#E53DE8',
  },
  dotGreen: {
    color: '#0FA65F',
  },
  dotLimeGreen: {
    color: '#03C04A',
  },
  dotBlack: {
    color: '#434E59',
  },
  dotPurple: {
    color: '#7A5AF8',
  },
  dotYellow: {
    color: '#F79009',
  },
  dotRed: {
    color: '#F04438',
  },
  dotBlue: {
    color: '#2E90FA',
  },
  dotDarkOrange: {
    color: '#BE5504',
  },
  dotPink: {
    color: '#E53DE8',
  },
}));

const AssignedTaskStatus = ({ estateWorkItemSubStatus, onClick }: Props) => {
  const classes = useStyles();

  const externalSubStatus = EstateWorkItemSubStatusExternalLabel[estateWorkItemSubStatus];

  if (!externalSubStatus) {
    return null;
  }

  const getLabel = () => {
    return externalSubStatus;
  };

  const getClassNames = () => {
    switch (estateWorkItemSubStatus) {
      case EstateWorkItemSubStatus.InProgress:
      case EstateWorkItemSubStatus.InProgressPrinting:
      case EstateWorkItemSubStatus.PreparingPaperwork:
        return { text: classes.chipPurple, dot: classes.dotPurple };
      case EstateWorkItemSubStatus.NotStarted:
      case EstateWorkItemSubStatus.UnderReview:
      case EstateWorkItemSubStatus.UserRequestFulfilled:
        return { text: classes.chipYellow, dot: classes.dotYellow };
      case EstateWorkItemSubStatus.InProgressWaitingOnClientInfo:
        return { text: classes.chipRed, dot: classes.dotRed };
      case EstateWorkItemSubStatus.InProgressWaitingOnClientSignature:
        return { text: classes.chipDarkYellow, dot: classes.dotYellow };
      case EstateWorkItemSubStatus.Stale:
        return { text: classes.chipRed, dot: classes.dotRed };
      case EstateWorkItemSubStatus.InProgressBankInquiry:
      case EstateWorkItemSubStatus.InProgressInsuranceInquiry:
      case EstateWorkItemSubStatus.InProgressWaitingOnInstitution:
        return { text: classes.chipBlue, dot: classes.dotBlue };
      case EstateWorkItemSubStatus.AutoGenerationInProgress:
        return { text: classes.chipPink, dot: classes.dotPink };
      case EstateWorkItemSubStatus.AutoGenerationAwaitingSignature:
        return { text: classes.chipDarkYellow, dot: classes.dotYellow };
      case EstateWorkItemSubStatus.AutoGenerationReadyToPrint:
        return { text: classes.chipLightGreen, dot: classes.dotLimeGreen };
      case EstateWorkItemSubStatus.AutoGenerationTemplateNeeded:
        return { text: classes.chipBlack, dot: classes.dotBlack };
      case EstateWorkItemSubStatus.AutoGenerationCompleted:
        return { text: classes.chipLimeGreen, dot: classes.dotLimeGreen };
      default:
        return { text: classes.chipBlack, dot: classes.dotBlack };
    }
  };

  const label = getLabel();
  const { text, dot } = getClassNames();

  return (
    <Chip
      className={clsx(classes.text, text)}
      icon={<Circle className={clsx(classes.dot, dot)} />}
      label={label}
      size="small"
      onClick={() => {
        onClick && onClick();
      }}
    />
  );
};

export default AssignedTaskStatus;
